/**
 * Delivery Info Component
 *
 * Stock data is read exclusively from the deliveryEl's data-* attributes,
 * which are SSR-rendered (Liquid has reliable access to inventory_quantity)
 * and synced by global.js after every variant change. The variant JSON blob
 * (<script type="application/json">) does NOT always include inventory_quantity,
 * so we never use it as a source for stock data.
 */

(function () {
  'use strict';

  function readState(deliveryEl) {
    return {
      stock: parseInt(deliveryEl.getAttribute('data-stock'), 10) || 0,
      policy: deliveryEl.getAttribute('data-inventory-policy') || 'continue',
      available: deliveryEl.getAttribute('data-available') === 'true',
      days: parseInt(deliveryEl.getAttribute('data-delivery-days'), 10) || 5,
    };
  }

  function updateUI(deliveryEl, quantity) {
    if (!deliveryEl) return;

    const { stock, policy, available, days } = readState(deliveryEl);
    const qty = quantity || 1;
    const hasStock = qty <= stock;

    deliveryEl.setAttribute('data-quantity', qty);

    deliveryEl.classList.remove('delivery-info--green', 'delivery-info--yellow', 'delivery-info--red');
    if (!available) deliveryEl.classList.add('delivery-info--red');
    else if (stock > 0 && hasStock) deliveryEl.classList.add('delivery-info--green');
    else if (policy === 'continue') deliveryEl.classList.add('delivery-info--yellow');
    else deliveryEl.classList.add('delivery-info--red');

    const text = deliveryEl.querySelector('[data-delivery-text]');
    if (text) {
      const supp = (str) => `<span class="delivery-info__supplement"> &middot; ${str}</span>`;
      if (!available) {
        text.innerHTML = 'Nicht auf Lager – aktuell nicht nachbestellbar';
      } else if (stock > 0 && hasStock) {
        // Deny in-stock: keep the dot+status row standard ("Sofort lieferbar"); the
        // Auslauf urgency is communicated by the prominent heading above the row.
        if (policy === 'deny') {
          text.innerHTML = '<span>Sofort lieferbar</span>';
        } else {
          text.innerHTML = `<span>Sofort lieferbar</span>${supp(`${stock} auf Lager`)}`;
        }
      } else if (policy === 'continue') {
        const back = stock > 0 ? qty - stock : qty;
        let html;
        if (days > 6) {
          const minD = Math.max(1, days - 1);
          const maxD = days + 1;
          html = `Lieferbar in <span>${minD} bis ${maxD} Arbeitstagen</span>`;
        } else {
          html = 'Lieferbar in <span>5 bis 7 Arbeitstagen</span>';
        }
        if (back > 0) html += supp(`${back} per Nachbestellung`);
        text.innerHTML = html;
      } else {
        // deny + qty > stock — user requested more than allowed (rare; form usually clamps).
        // Heading above already shows "Auslaufartikel · Nur noch X verfügbar".
        text.innerHTML = '<span>Sofort lieferbar</span>';
      }
    }

    if (typeof window.craftDepotUpdateSameDayShipping === 'function') {
      window.craftDepotUpdateSameDayShipping();
    }
  }

  // Apply max attribute to quantity input from SSR-rendered data attrs on the deliveryEl.
  // We also respect any existing max attribute (set by SSR from quantity_rule.max).
  function applyQuantityMax(quantityInput, deliveryEl) {
    if (!quantityInput || !deliveryEl) return;
    const { stock, policy } = readState(deliveryEl);
    const ruleMax = parseInt(quantityInput.getAttribute('data-max'), 10);

    if (policy === 'deny' && stock > 0) {
      const max = !isNaN(ruleMax) && ruleMax > 0 ? Math.min(stock, ruleMax) : stock;
      quantityInput.setAttribute('max', max);
    } else if (!isNaN(ruleMax) && ruleMax > 0) {
      quantityInput.setAttribute('max', ruleMax);
    } else {
      quantityInput.removeAttribute('max');
    }

    const cur = parseInt(quantityInput.value, 10) || 1;
    const max = parseInt(quantityInput.getAttribute('max'), 10);
    if (!isNaN(max) && cur > max) quantityInput.value = max;

    updatePlusButtonState(quantityInput);
  }

  function clampToMax(quantityInput) {
    const max = parseInt(quantityInput.getAttribute('max'), 10);
    const value = parseInt(quantityInput.value, 10);
    if (!isNaN(max) && value > max) {
      quantityInput.value = max;
      quantityInput.classList.remove('quantity__input--exceeded');
      void quantityInput.offsetWidth;
      quantityInput.classList.add('quantity__input--exceeded');
      setTimeout(() => quantityInput.classList.remove('quantity__input--exceeded'), 450);
      return true;
    }
    return false;
  }

  function updatePlusButtonState(quantityInput) {
    const wrap = quantityInput.closest('quantity-input');
    if (!wrap) return;
    const plus = wrap.querySelector('button[name="plus"]');
    if (!plus) return;
    const max = parseInt(quantityInput.getAttribute('max'), 10);
    const cur = parseInt(quantityInput.value, 10) || 1;
    if (!isNaN(max) && cur >= max) plus.setAttribute('disabled', '');
    else plus.removeAttribute('disabled');
  }

  function getRoot(deliveryEl) {
    return deliveryEl.closest('floated-form') || deliveryEl.closest('div[data-section]') || document;
  }

  function init(deliveryEl) {
    if (deliveryEl.dataset.dInit) return;
    deliveryEl.dataset.dInit = '1';

    const root = getRoot(deliveryEl);
    const quantityInput = root.querySelector('.quantity__input');

    if (quantityInput) applyQuantityMax(quantityInput, deliveryEl);
    updateUI(deliveryEl, quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1);

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        updateUI(deliveryEl, parseInt(quantityInput.value, 10) || 1);
        if (clampToMax(quantityInput)) {
          updateUI(deliveryEl, parseInt(quantityInput.value, 10) || 1);
        }
        updatePlusButtonState(quantityInput);
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-delivery-info]').forEach(init);
  }

  // <floated-form> populates its own innerHTML after an async fetch (see
  // main-product.js). Observe each one in isolation and disconnect once its
  // delivery-info element appears, instead of watching the whole document.
  function watchFloatedForms() {
    document.querySelectorAll('floated-form').forEach((form) => {
      if (form.dataset.dObserved) return;
      form.dataset.dObserved = '1';
      const obs = new MutationObserver(() => {
        const deliveryEl = form.querySelector('[data-delivery-info]');
        if (deliveryEl) {
          init(deliveryEl);
          obs.disconnect();
        }
      });
      obs.observe(form, { childList: true, subtree: true });
    });
  }

  function bootstrap() {
    initAll();
    watchFloatedForms();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Public API — global.js calls these AFTER syncing data-* attrs from SSR response.
  window.craftDepotDeliveryInfo = {
    update: updateUI,
    applyQuantityMax: applyQuantityMax,
    updatePlusButtonState: updatePlusButtonState,
  };
})();
